import { useState, useEffect, useRef } from 'react';
import { useReactToPrint } from 'react-to-print';
import { llcApi, checkApi, payeeApi, checkLayoutApi } from '../api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Switch } from '../components/ui/switch';
import { CalendarIcon, Printer, FileText, AlertTriangle } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { CheckPrintView } from '../components/CheckPrintView';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';

export const CreateCheckPage = () => {
  const [llcs, setLlcs] = useState([]);
  const [payees, setPayees] = useState([]);
  const [selectedLLC, setSelectedLLC] = useState(null);
  const [selectedPayeeId, setSelectedPayeeId] = useState('');
  const [customPayee, setCustomPayee] = useState('');
  const [useCustomPayee, setUseCustomPayee] = useState(false);
  const [amount, setAmount] = useState('');
  const [memo, setMemo] = useState('');
  const [message, setMessage] = useState('');
  const [date, setDate] = useState(new Date());
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [createdCheck, setCreatedCheck] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [isPreprintedCheck, setIsPreprintedCheck] = useState(false);
  const [preprintedCheckNumber, setPreprintedCheckNumber] = useState('');
  const [showPrintConfirm, setShowPrintConfirm] = useState(false);
  const [pendingPrintAction, setPendingPrintAction] = useState(null);
  const [checkLayout, setCheckLayout] = useState(null);
  const [duplicateWarnings, setDuplicateWarnings] = useState([]);
  
  const printRef = useRef(null);
  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Check-${createdCheck?.check_number || 'preview'}`,
  });

  useEffect(() => {
    fetchLLCs();
    checkLayoutApi.get().then(res => setCheckLayout(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (selectedLLC) {
      fetchPayees(selectedLLC.id);
    }
  }, [selectedLLC]);

  // Duplicate check detection
  useEffect(() => {
    if (!selectedLLC) { setDuplicateWarnings([]); return; }
    const payeeName = useCustomPayee ? customPayee : payees.find(p => p.id === selectedPayeeId)?.name;
    const parsedAmount = parseFloat(amount);
    const checkNum = isPreprintedCheck ? parseInt(preprintedCheckNumber, 10) : null;
    const hasData = (payeeName && !isNaN(parsedAmount)) || (checkNum && !isNaN(checkNum));
    if (!hasData) { setDuplicateWarnings([]); return; }
    const timer = setTimeout(async () => {
      try {
        const res = await checkApi.checkDuplicates({
          llc_id: selectedLLC.id,
          check_number: checkNum && !isNaN(checkNum) ? checkNum : undefined,
          payee: payeeName || undefined,
          amount: !isNaN(parsedAmount) ? parsedAmount : undefined,
        });
        setDuplicateWarnings(res.data.warnings || []);
      } catch { setDuplicateWarnings([]); }
    }, 500);
    return () => clearTimeout(timer);
  }, [selectedLLC, selectedPayeeId, customPayee, useCustomPayee, amount, preprintedCheckNumber, isPreprintedCheck, payees]);


  const fetchLLCs = async () => {
    try {
      const response = await llcApi.list();
      setLlcs(response.data);
      if (response.data.length > 0) {
        setSelectedLLC(response.data[0]);
      }
    } catch (error) {
      toast.error('Failed to fetch LLCs');
    } finally {
      setLoading(false);
    }
  };

  const fetchPayees = async (llcId) => {
    try {
      const response = await payeeApi.list(llcId);
      setPayees(response.data);
      // Reset payee selection when LLC changes
      setSelectedPayeeId('');
      setCustomPayee('');
      setUseCustomPayee(false);
    } catch (error) {
      console.error('Failed to fetch payees:', error);
      setPayees([]);
    }
  };

  const handleLLCChange = (llcId) => {
    const llc = llcs.find(l => l.id === llcId);
    setSelectedLLC(llc);
    setCreatedCheck(null);
    if (isPreprintedCheck && llc) {
      setPreprintedCheckNumber(String(llc.next_check_number));
    }
  };

  const handlePayeeChange = (value) => {
    if (value === '__custom__') {
      setUseCustomPayee(true);
      setSelectedPayeeId('');
    } else {
      setUseCustomPayee(false);
      setSelectedPayeeId(value);
      setCustomPayee('');
    }
  };

  const getPayeeName = () => {
    if (useCustomPayee) {
      return customPayee;
    }
    const payee = payees.find(p => p.id === selectedPayeeId);
    return payee?.name || '';
  };

  const getSelectedPayee = () => {
    if (useCustomPayee) {
      return { name: customPayee };
    }
    return payees.find(p => p.id === selectedPayeeId) || { name: '' };
  };

  const handleAmountChange = (e) => {
    const value = e.target.value.replace(/[^0-9.]/g, '');
    setAmount(value);
  };

  const handleAmountBlur = () => {
    if (amount) {
      const num = parseFloat(amount);
      if (!isNaN(num)) {
        setAmount(num.toFixed(2));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedLLC) {
      toast.error('Please select an LLC');
      return;
    }
    
    const payeeName = getPayeeName();
    if (!payeeName) {
      toast.error('Please select or enter a payee');
      return;
    }
    
    const payee = getSelectedPayee();
    
    setSubmitting(true);
    try {
      const checkPayload = {
        llc_id: selectedLLC.id,
        payee: payeeName,
        payee_address_line1: payee.address_line1 || '',
        payee_address_line2: payee.address_line2 || '',
        payee_city_state_zip: payee.city && payee.state ? `${payee.city}, ${payee.state} ${payee.zip_code || ''}`.trim() : '',
        amount: parseFloat(amount),
        memo,
        message,
        date: date.toISOString(),
      };
      
      if (isPreprintedCheck && preprintedCheckNumber) {
        checkPayload.check_number = parseInt(preprintedCheckNumber);
        checkPayload.is_preprinted = true;
      }
      
      const response = await checkApi.create(checkPayload);
      setCreatedCheck(response.data);
      toast.success(`Check #${response.data.check_number} created successfully`);
      
      // Update the selected LLC's next check number locally
      const nextNum = response.data.check_number + 1;
      setSelectedLLC(prev => ({
        ...prev,
        next_check_number: nextNum,
      }));
      // Auto-increment preprinted check number for next check
      if (isPreprintedCheck) {
        setPreprintedCheckNumber(String(nextNum));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create check');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePrintAndMark = async () => {
    const checkNum = createdCheck?.check_number || selectedLLC?.next_check_number;
    if (isPreprintedCheck) {
      setPendingPrintAction('printAndMark');
      setShowPrintConfirm(true);
      return;
    }
    executePrintAndMark();
  };

  const executePrintAndMark = async () => {
    handlePrint();
    if (createdCheck) {
      try {
        await checkApi.updateStatus(createdCheck.id, 'printed');
        setCreatedCheck(prev => ({ ...prev, status: 'printed' }));
      } catch (error) {
        console.error('Failed to update status:', error);
      }
    }
  };

  const handlePrintSample = () => {
    if (isPreprintedCheck) {
      setPendingPrintAction('sample');
      setShowPrintConfirm(true);
      return;
    }
    handlePrint();
  };

  const handlePrintConfirmed = () => {
    setShowPrintConfirm(false);
    if (pendingPrintAction === 'printAndMark') {
      executePrintAndMark();
    } else {
      handlePrint();
    }
    setPendingPrintAction(null);
  };

  const resetForm = () => {
    setSelectedPayeeId('');
    setCustomPayee('');
    setUseCustomPayee(false);
    setAmount('');
    setMemo('');
    setMessage('');
    setDate(new Date());
    setCreatedCheck(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-slate-500">Loading...</div>
      </div>
    );
  }

  if (llcs.length === 0) {
    return (
      <div>
        <Card className="border-slate-200">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <FileText className="w-12 h-12 text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-900">No LLCs Available</h3>
            <p className="text-slate-500 mt-1">Please add an LLC first before creating checks</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-3 fade-in" data-testid="create-check-page">
      <div>
        <h1 className="text-lg font-semibold text-slate-900 tracking-tight">Create Check</h1>
        <p className="text-slate-500 mt-1">Fill in the check details and print</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
        {/* Check Form */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle>Check Details</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label htmlFor="llc">Select LLC</Label>
                <Select value={selectedLLC?.id} onValueChange={handleLLCChange}>
                  <SelectTrigger data-testid="check-llc-select">
                    <SelectValue placeholder="Select an LLC" />
                  </SelectTrigger>
                  <SelectContent>
                    {llcs.map(llc => (
                      <SelectItem key={llc.id} value={llc.id}>
                        {llc.company_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedLLC && (
                  <p className="text-xs text-slate-500 mt-1">
                    Next check number: <span className="font-mono font-semibold">{selectedLLC.next_check_number}</span>
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="date">Date</Label>
                <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                      data-testid="check-date-btn"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(date, 'PPP')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={(d) => {
                        setDate(d || new Date());
                        setCalendarOpen(false);
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div>
                <Label htmlFor="payee">Pay to the Order of</Label>
                {!useCustomPayee ? (
                  <Select 
                    value={selectedPayeeId || '__custom__'} 
                    onValueChange={handlePayeeChange}
                  >
                    <SelectTrigger data-testid="check-payee-select">
                      <SelectValue placeholder="Select a payee" />
                    </SelectTrigger>
                    <SelectContent>
                      {payees.map(payee => (
                        <SelectItem key={payee.id} value={payee.id}>
                          {payee.name}
                        </SelectItem>
                      ))}
                      <SelectItem value="__custom__" className="text-slate-500 border-t mt-1 pt-1">
                        + Enter custom payee...
                      </SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="space-y-2">
                    <Input
                      id="customPayee"
                      value={customPayee}
                      onChange={(e) => setCustomPayee(e.target.value)}
                      placeholder="Enter payee name"
                      data-testid="check-custom-payee-input"
                    />
                    <Button 
                      type="button" 
                      variant="link" 
                      size="sm" 
                      className="h-auto p-0 text-xs"
                      onClick={() => {
                        setUseCustomPayee(false);
                        setCustomPayee('');
                      }}
                    >
                      ← Back to payee list
                    </Button>
                  </div>
                )}
                {payees.length === 0 && !useCustomPayee && (
                  <p className="text-xs text-amber-600 mt-1">
                    No payees assigned to this LLC. Add payees in Payee Management or enter a custom name.
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="amount">Amount ($)</Label>
                <Input
                  id="amount"
                  value={amount}
                  onChange={handleAmountChange}
                  onBlur={handleAmountBlur}
                  placeholder="0.00"
                  className="font-mono text-lg"
                  required
                  data-testid="check-amount-input"
                />
              </div>

              <div>
                <Label htmlFor="memo">Memo (Optional)</Label>
                <Input
                  id="memo"
                  value={memo}
                  onChange={(e) => setMemo(e.target.value)}
                  placeholder="For invoice #, etc."
                  data-testid="check-memo-input"
                />
              </div>

              <div>
                <Label htmlFor="message">Message (Optional)</Label>
                <Input
                  id="message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="General message for mailing panel"
                  data-testid="check-message-input"
                />
              </div>

              <div className="pt-3 border-t border-slate-100">
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="preprinted" className="text-sm font-medium">Preprinted Check</Label>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {isPreprintedCheck 
                        ? "Won't print company, bank, or check number" 
                        : "Will print all check information"}
                    </p>
                  </div>
                  <Switch
                    id="preprinted"
                    checked={isPreprintedCheck}
                    onCheckedChange={(checked) => {
                      setIsPreprintedCheck(checked);
                      if (checked && selectedLLC) {
                        setPreprintedCheckNumber(String(selectedLLC.next_check_number));
                      }
                    }}
                    data-testid="preprinted-check-switch"
                  />
                </div>
                {isPreprintedCheck && (
                  <div className="mt-3">
                    <Label htmlFor="checkNumber">Check Number</Label>
                    <Input
                      id="checkNumber"
                      type="number"
                      value={preprintedCheckNumber}
                      onChange={(e) => setPreprintedCheckNumber(e.target.value)}
                      placeholder="Enter check number"
                      className="font-mono"
                      required
                      data-testid="preprinted-check-number-input"
                    />
                  </div>
                )}
              </div>

              {/* Duplicate Warnings */}
              {duplicateWarnings.length > 0 && (
                <div className="space-y-2" data-testid="duplicate-warnings">
                  {duplicateWarnings.map((w, i) => (
                    <div key={i} className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-300 rounded-lg">
                      <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-amber-800">{w.message}</span>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <Button type="submit" disabled={submitting} className="flex-1" data-testid="check-create-btn">
                  {submitting ? 'Creating...' : 'Create Check'}
                </Button>
                {createdCheck && (
                  <Button type="button" variant="outline" onClick={resetForm}>
                    New Check
                  </Button>
                )}
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Check Preview */}
        <div>
          <Card className="border-slate-200">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Check Preview</CardTitle>
              <div className="flex gap-2">
                {selectedLLC && !createdCheck && (
                  <Button variant="outline" onClick={handlePrintSample} data-testid="check-print-sample-btn">
                    <Printer className="w-4 h-4 mr-2" />
                    Print Sample
                  </Button>
                )}
                {createdCheck && (
                  <Button onClick={handlePrintAndMark} data-testid="check-print-btn">
                    <Printer className="w-4 h-4 mr-2" />
                    Print Check
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {isPreprintedCheck && (
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2" data-testid="preprinted-notice">
                  <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                  <div className="text-sm text-amber-800">
                    <span className="font-semibold">Preprinted Check Stock Required</span>
                    <p className="text-xs text-amber-600 mt-0.5">Load check #{isPreprintedCheck && preprintedCheckNumber ? preprintedCheckNumber : createdCheck?.check_number || selectedLLC?.next_check_number} into your printer before printing.</p>
                  </div>
                </div>
              )}
              {selectedLLC ? (
                <div className="check-preview-wrapper overflow-visible">
                  <div className="transform scale-[0.85] origin-top-left" style={{ width: '118%', height: 'auto' }}>
                    <CheckPrintView
                      ref={printRef}
                      llc={selectedLLC}
                      check={createdCheck || (() => {
                        const payee = getSelectedPayee();
                        return {
                          check_number: isPreprintedCheck && preprintedCheckNumber 
                            ? parseInt(preprintedCheckNumber) || selectedLLC.next_check_number 
                            : selectedLLC.next_check_number,
                          date: date.toISOString(),
                          payee: payee.name || 'Payee Name',
                          payee_address_line1: payee.address_line1 || '',
                          payee_address_line2: payee.address_line2 || '',
                          payee_city_state_zip: payee.city && payee.state ? `${payee.city}, ${payee.state} ${payee.zip_code || ''}`.trim() : '',
                          amount: parseFloat(amount) || 0,
                          memo: memo,
                          message: message,
                        };
                      })()}
                      isPreview={!createdCheck}
                      isPreprinted={isPreprintedCheck}
                      layout={checkLayout}
                    />
                  </div>
                </div>
              ) : (
                <div className="h-64 flex items-center justify-center text-slate-400">
                  Select an LLC to preview check
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Preprinted Check Print Confirmation Dialog */}
      <AlertDialog open={showPrintConfirm} onOpenChange={setShowPrintConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Printer className="w-5 h-5" />
              Load Preprinted Check Stock
            </AlertDialogTitle>
            <AlertDialogDescription className="text-base">
              Please load check <span className="font-mono font-bold text-slate-900">#{createdCheck?.check_number || preprintedCheckNumber || selectedLLC?.next_check_number}</span> into your printer, then click Print to continue.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handlePrintConfirmed} data-testid="confirm-print-btn">
              Print
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
